import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { IconClose, IconSave } from "@synthesis/shared-icons";
import { getProfileQueryKey, useUpdateProfile } from "@synthesis/shared-services/external/profile/hooks";
import type { UpdateProfileData } from "@synthesis/shared-services/external/profile/types";
import { Button, Input, setPromiseSnack } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import { userInfoEditFormSchema, type UserInfoEditFormValues } from "../user-info-page.lib";

import { ChangeEmailModal } from "./change-email-modal";

type UserInfoEditViewProps = {
  organization: string;
  position: string;
  contactPhone: string;
  corporateEmail: string;
  lastName: string;
  firstName: string;
  middleName: string;
  loginEmail: string;
  onCancel: () => void;
  onSaved: () => void;
};

/**
 * PUT /api/external/profile replaces work fields. Empty strings clear string fields;
 * corporateEmail is omitted when empty because generated z.email() rejects "".
 */
const buildUpdateProfileData = (values: UserInfoEditFormValues): UpdateProfileData => {
  const corporateEmail = values.corporateEmail.trim();

  return {
    organization: values.organization.trim(),
    position: values.position.trim(),
    contactPhone: values.contactPhone.trim(),
    ...(corporateEmail ? { corporateEmail } : {}),
  };
};

export const UserInfoEditView = ({
  organization,
  position,
  contactPhone,
  corporateEmail,
  lastName,
  firstName,
  middleName,
  loginEmail,
  onCancel,
  onSaved,
}: UserInfoEditViewProps) => {
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const updateProfileMutation = useUpdateProfile();

  const methods = useForm<UserInfoEditFormValues>({
    resolver: zodResolver(userInfoEditFormSchema),
    mode: "onChange",
    defaultValues: {
      organization,
      position,
      contactPhone,
      corporateEmail,
      lastName,
      firstName,
      middleName,
    },
  });

  const { isValid, isDirty } = methods.formState;
  const isSaving = updateProfileMutation.isPending;
  const canSave = isValid && isDirty && !isSaving;

  const handleSave = methods.handleSubmit((values) => {
    if (isSaving) {
      return;
    }

    const promise = (async () => {
      const updated = await updateProfileMutation.mutateAsync({
        body: buildUpdateProfileData(values),
      });
      queryClient.setQueryData(getProfileQueryKey(), updated);
      onSaved();
    })();

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: {
          pending: "Сохранение изменений",
          fulfilled: "Изменения сохранены",
          rejected: "Не удалось сохранить изменения",
        },
      },
    });
  });

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-8 px-4 py-6 md:py-8">
        <section className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <h2 className="paragraph-lg text-type-common">Личные и рабочие данные</h2>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                theme="text"
                size="sm"
                leftIcon={<IconClose className="text-neutrals-60 group-disabled:text-neutrals-80 size-5" />}
                onClick={onCancel}
                disabled={isSaving}
              >
                Отменить
              </Button>
              <Button
                type="button"
                theme="text"
                size="sm"
                leftIcon={<IconSave className="text-neutrals-60 group-disabled:text-neutrals-80 size-5" />}
                disabled={!canSave}
                onClick={handleSave}
              >
                Сохранить
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
            <Input name="organization" label="Организация" placeholder="Укажите наименование организации" size="lg" />
            <Input name="lastName" label="Фамилия" placeholder="Укажите фамилию" size="lg" disabled />
            <Input name="position" label="Должность" placeholder="Укажите должность" size="lg" />
            <Input name="firstName" label="Имя" placeholder="Укажите имя" size="lg" disabled />
            <Input name="contactPhone" label="Контактный телефон" placeholder="Введите контактный телефон" size="lg" />
            <Input name="middleName" label="Отчество" placeholder="Укажите отчество" size="lg" disabled />
            <Input name="corporateEmail" label="Корпоративная почта" placeholder="example@mail.com" size="lg" />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="paragraph-lg text-type-common">Аккаунт</h2>
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <div className="flex w-[438px] min-w-0 flex-col gap-1">
                <p className="subtitle-sm text-type-light">Логин / E-mail</p>
                <p className="paragraph-base text-type-common break-words">{loginEmail}</p>
              </div>
              <Button type="button" theme="secondary" size="sm" className="shrink-0" onClick={() => setIsChangeEmailModalOpen(true)}>
                Изменить
              </Button>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <h2 className="paragraph-lg text-type-common">Деактивировать учётную запись</h2>
          <p className="subtitle-sm text-type-minor max-w-[40rem]">
            После деактивации вы не сможете войти в систему. Ваши заявки и история сохранятся в соответствии с регламентом.
          </p>
          <Button type="button" theme="primary" size="md" className="w-fit">
            Деактивировать
          </Button>
        </section>
      </div>

      <ChangeEmailModal isOpen={isChangeEmailModalOpen} onOpenChange={setIsChangeEmailModalOpen} />
    </FormProvider>
  );
};
